import { useState } from 'react';
import { ApiError } from '@/api/client';
import { Icon } from '@/components/Icon';
import { qty } from '@/lib/format';
import { closeOrderException } from './api';
import base from './OrderExceptionCreateDialog.module.css';
import s from './OrderExceptionCloseDialog.module.css';

type CloseItem = {
  id: string;
  status: string;
  type: string;
  quantity?: number | string | null;
  product?: { name: string } | null;
  sourceBundleName?: string | null;
  note?: string | null;
  resolutionAction?: string | null;
  resolutionNote?: string | null;
};
const READONLY = import.meta.env.VITE_READONLY !== '0';
const actions = new Set(['NOTED', 'RESHIPPED', 'REFUNDED', 'IGNORED', 'TRANSFERRED']);

export function OrderExceptionCloseDialog({ order, item, onClose, onSuccess }: { order: { id: string; orderNo: string }; item: CloseItem; onClose: () => void; onSuccess: () => void }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const pending = item.status === 'PENDING';
  const close = () => { if (done) onSuccess(); onClose(); };
  const submit = async () => {
    if (READONLY || saving || uncertain || !note.trim() || !['PENDING', 'PROCESSING'].includes(item.status)) return;
    setSaving(true); setError('');
    try {
      const previousAction = item.resolutionAction;
      await closeOrderException(order.id, item.id, {
        status: 'CLOSED',
        resolutionAction: previousAction && actions.has(previousAction) ? previousAction as 'NOTED' | 'RESHIPPED' | 'REFUNDED' | 'IGNORED' | 'TRANSFERRED' : 'NOTED',
        resolutionNote: item.resolutionNote ? `${item.resolutionNote}\n关闭说明：${note.trim()}` : note.trim(),
      });
      setDone(true);
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '关闭异常失败');
    } finally { setSaving(false); }
  };
  return <div className={`${base.backdrop} ${s.backdrop}`} onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) close(); }}>
    <section className={`${base.dialog} ${s.dialog}`} role="dialog" aria-modal="true" aria-label="关闭异常">
      <header className={`${base.head} ${s.head}`}><button type="button" onClick={close} disabled={saving} aria-label="取消并返回">×</button><div><b>关闭异常？</b><small>{order.orderNo}</small></div><span /></header>
      {done ? <div className={base.done}><Icon name="check" size={24} /><b>异常已关闭</b><p>共享异常记录已更新，V1 也能看到并继续查看。</p><button type="button" onClick={close}>完成</button></div> : <>
        <div className={base.body}>
          <div className={s.warning}><Icon name="alert" size={18} /><div><p>关闭后这条异常<strong>不再出现在未闭环列表</strong>，但记录和损失统计保留。</p><p>通常在补发已发出、退货已入库、或与客户协商完毕后关闭。</p>{pending && <p><b>当前这条还是「待处理」状态，直接关闭意味着不做任何补偿动作。</b></p>}</div></div>
          <div className={s.summary}><b>会保留：记录、凭证照片和损失统计。</b><span>{item.product?.name || item.sourceBundleName || '订单级异常'} · {item.type} · {qty(item.quantity)} 个</span>{item.note && <p>{item.note}</p>}{item.resolutionNote && <p>已有处理说明：{item.resolutionNote}</p>}</div>
          <div className={`${base.group} ${s.reasonGroup}`}><label><em>*</em> 关闭原因<textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder="例如：客户表示不需要补发" /></label><div className={s.chips}>{['客户表示不需要补发', '已协商完毕', '重复登记'].map((reason) => <button type="button" key={reason} className={note === reason ? s.selected : ''} onClick={() => setNote(reason)}>{reason}</button>)}</div><small>关闭会保留此前的处理说明，并记录本次关闭原因。</small></div>
          {READONLY && <p className={base.notice}>当前为生产只读模式，可查看窗口，暂不能关闭异常。</p>}
          {error && <p className={base.error} role="alert">{error}{uncertain && '。结果未确认，请先刷新订单核对状态，暂勿重复提交。'}</p>}
        </div>
        <footer className={`${base.foot} ${s.foot}`}><button className={s.continue} type="button" onClick={close}><span className={s.desktopCancel}>取消</span><span className={s.mobileContinue}>继续处理</span></button><button className={s.confirm} type="button" disabled={READONLY || saving || uncertain || !note.trim()} onClick={() => void submit()}>{saving ? '关闭中…' : <><span className={s.desktopConfirm}>确认关闭</span><span className={s.mobileConfirm}>关闭异常</span></>}</button></footer>
      </>}
    </section>
  </div>;
}
